import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Stack,
  Switch,
  TextareaAutosize,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../theme";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../../service/Api";
import { MemoryBook } from "../../../../../types/memory-table";
import { toast } from "react-toastify";
import { showErrorToast } from "../../../../../components/common/ShowToast";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

type ActiveTabType =
  | "tribute-comments"
  | "youtube-comments"
  | "media"
  | "upload"
  | "delivery";

interface Props {
  setActiveTab: React.Dispatch<React.SetStateAction<ActiveTabType>>;
}

export const MemoryBookDelivery: React.FC<Props> = ({ setActiveTab }) => {
  const { id } = useParams();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  //const reduxTheme = useSelector((state: RootState) => state.auth.user.);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [deliveredTo, setDeliveredTo] = useState<string>("");
  const [deliveredBy, setDeliveredBy] = useState<string>("");
  const [courierName, setCourierName] = useState<string>("");
  const [deliverySwitchChecked, setDeliverySwitchChecked] =
    useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [memoryBooks, setMemoryBooks] = useState<MemoryBook[]>([]);
  const [deliverOptions, setDeliverOptions] = useState<string[]>([]);

  const fetchMemoryBooks = useCallback(async () => {
    setIsLoading(true);
    setError(false);

    try {
      const response = await apiService.get<{ results: MemoryBook[] }>(
        `/api/streams/memory-book/?stream=${id}`
      );

      if (response.data.results.length > 0) {
        const memoryBook = response.data.results[0];
        setDeliverySwitchChecked(memoryBook.is_delivered);
        setNotes(memoryBook.delivery_notes || memoryBook.notes || "");
        setCourierName(memoryBook.courier_name || "");
        setDeliveredTo(memoryBook.delivered_to || "");
        setEmail(memoryBook.delivery_email || "");
        const nextOptions: string[] = [];
        if (memoryBook.courier_name) {
          nextOptions.push("courier");
        }
        if (memoryBook.delivered_to) {
          nextOptions.push("in_person");
        }
        if (memoryBook.delivery_email) {
          nextOptions.push("email");
        }
        setDeliverOptions(nextOptions);
      }

      setMemoryBooks(response.data.results);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load memory books. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMemoryBooks();
  }, [fetchMemoryBooks]);

  const updateDeliveryDetails = async () => {
    setIsSubmitting(true);

    // data integrity checks
    if (deliverOptions.length === 0 && deliverySwitchChecked) {
      showErrorToast("You must select at least one delivery method.");
      setIsSubmitting(false);
      return;
    }

    if (deliverOptions.includes("email") && email.length === 0) {
      showErrorToast(
        "You selected email as a delivery method but did not provide an email address."
      );
      setIsSubmitting(false);
      return;
    }

    if (deliverOptions.includes("in_person") && deliveredTo.length === 0) {
      showErrorToast(
        "You selected in-person delivery but did not provide a delivery to name."
      );
      setIsSubmitting(false);
      return;
    }

    if (deliverOptions.includes("courier") && courierName.length === 0) {
      showErrorToast(
        "You selected courier delivery but did not provide a courier name."
      );
      setIsSubmitting(false);
      return;
    }

    //

    try {
      const currentBook: MemoryBook = memoryBooks[0];

      const formData = new FormData();
      formData.append("is_delivered", `${deliverySwitchChecked}`);
      formData.append("delivery_methods", JSON.stringify(deliverOptions));
      formData.append("delivery_notes", notes);

      // add data based on checked options
      if (deliverOptions.includes("email")) {
        formData.append("delivery_email", email);
      }

      if (
        deliverOptions.includes("in_person") ||
        deliverOptions.includes("courier")
      ) {
        formData.append("delivered_to", deliveredTo);
        formData.append("delivered_by", deliveredBy);
      }

      if (deliverOptions.includes("courier")) {
        formData.append("courier_name", courierName);
      }

      // resetting data based on unchecked options

      if (
        !deliverOptions.includes("in_person") &&
        !deliverOptions.includes("courier")
      ) {
        formData.append("delivered_to", "");
        formData.append("delivered_by", "");
      }

      if (!deliverOptions.includes("courier")) {
        formData.append("courier_name", "");
      }

      if (!deliverOptions.includes("email")) {
        formData.append("delivery_email", "");
      }

      await apiService.patch(
        `/api/streams/memory-book/${currentBook.id}/`,
        formData
      );

      toast.success("Detailed updated successfully.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to update details.Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeliveryOptionChange = (option: string) => {
    setDeliverOptions((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  const handleDeliveredToggle = () => {
    if (deliverySwitchChecked) {
      setDeliverOptions([]);
      setEmail("");

      setCourierName("");
      setDeliveredTo("");
    }
    setDeliverySwitchChecked(!deliverySwitchChecked);
  };

  return (
    <Box p={2} borderRadius={2} bgcolor={colors.primary[900]}>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2em",
          }}
        >
          <CircularProgress size={25} />
        </Box>
      )}

      {error && !isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2em",
            flexDirection: "column",
          }}
        >
          <Typography variant="body1" sx={{ mb: 1, fontSize: 13 }}>
            Failed to fetch memory book
          </Typography>
          <Button variant="contained" onClick={fetchMemoryBooks}>
            Retry
          </Button>
        </Box>
      )}

      {!error && !isLoading && memoryBooks.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2em",
            flexDirection: "column",
          }}
        >
          <Typography variant="body1" sx={{ mb: 1, fontSize: 13 }}>
            No memory book has been upload yet.Please upload one to access this
            menu
          </Typography>
          <Button variant="contained" onClick={() => setActiveTab("upload")}>
            Upload
          </Button>
        </Box>
      )}
      {!error && !isLoading && memoryBooks.length > 0 && (
        <>
          <Grid container spacing={2} mt={3} mb={2}>
            <Grid
              size={6}
              sx={{
                border: `1px solid ${colors.primary[800]}`,
                //height: 200,
                borderRadius: 3,
                padding: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: ".5em",
                  bgcolor: colors.primary[800],
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Delivered
                </Typography>

                <Switch
                  checked={deliverySwitchChecked}
                  onClick={handleDeliveredToggle}
                />
              </Box>

              <Stack direction={"row"} spacing={5} sx={{ mt: 2 }}>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      onClick={() => handleDeliveryOptionChange("email")}
                      checked={deliverOptions.includes("email")}
                    />
                    <Typography variant="body1">Email</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    onClick={() => handleDeliveryOptionChange("in_person")}
                    checked={deliverOptions.includes("in_person")}
                  />
                  <Typography variant="body1">In person</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    onClick={() => handleDeliveryOptionChange("courier")}
                    checked={deliverOptions.includes("courier")}
                  />
                  <Typography variant="body1">Courier</Typography>
                </Box>
              </Stack>

              {deliverOptions.includes("email") && (
                <TextField
                  required
                  value={email}
                  id="outlined-required"
                  label="Email"
                  placeholder="Enter email you delivered to"
                  sx={{ mt: 2, width: "100%" }}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}

              {deliverOptions.includes("in_person") && (
                <>
                  <TextField
                    required
                    value={deliveredBy}
                    id="outlined-required"
                    label="Delivered by"
                    placeholder="Enter name of person who delivered"
                    sx={{ mt: 2, width: "100%" }}
                    onChange={(e) => setDeliveredBy(e.target.value)}
                  />

                  <TextField
                    required
                    value={deliveredTo}
                    id="outlined-required"
                    label="Delivered to"
                    placeholder="Enter name of person you delivered to"
                    sx={{ mt: 2, width: "100%" }}
                    onChange={(e) => setDeliveredTo(e.target.value)}
                  />
                </>
              )}

              {deliverOptions.includes("courier") && (
                <>
                  <TextField
                    required
                    value={courierName}
                    id="outlined-required"
                    label="Courier Name"
                    placeholder="Enter name of the courier you hired"
                    sx={{ mt: 2, width: "100%" }}
                    onChange={(e) => setCourierName(e.target.value)}
                  />
                </>
              )}

              {/* <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel id="delivery-method-select">
                  Delivery Method
                </InputLabel>
                <Select
                  labelId="delivery-method-select"
                  id="delivery-method-select"
                  value={deliveryMethod}
                  label="Delivery Method"
                  onChange={handleChange}
                >
                  <MenuItem value={"email"}>Email</MenuItem>
                  <MenuItem value={"in-person"}>In person</MenuItem>
                  <MenuItem value={"courier"}>Courier</MenuItem>
                </Select>
              </FormControl> */}

              <Box
                sx={{
                  padding: ".5em",
                  border: `1px solid ${colors.primary[800]}`,
                  mt: 2,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontSize: 10, mb: 1 }}>
                  Delivered by
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Delivered by
                </Typography>
              </Box>
            </Grid>

            <Grid size={6}>
              <TextareaAutosize
                onChange={(e) => setNotes(e.target.value)}
                value={notes}
                aria-placeholder="Add any special instructions or notes"
                placeholder="Add any special instructions or notes"
                style={{
                  width: "100%",
                  resize: "none",
                  padding: "0.5rem",
                  height: 234,
                  maxHeight: 234,
                  borderRadius: 4,
                  backgroundColor: colors.primary[800],
                  border: "none",
                }}
              />
            </Grid>
          </Grid>

          <Button
            variant="contained"
            sx={{ mt: 3, mb: 3, ml: 1 }}
            onClick={updateDeliveryDetails}
          >
            {isSubmitting ? (
              <CircularProgress color="secondary" size={22} />
            ) : (
              "Update"
            )}
          </Button>
        </>
      )}
    </Box>
  );
};

MemoryBookDelivery.propTypes = {
  setActiveTab: PropTypes.func.isRequired,
};
